import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import {
	CloseOutlined,
	HourglassOutlined,
	LoadingOutlined,
	MessageOutlined,
	RollbackOutlined,
} from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	stopDefaultEvents,
	isSupplierPayingCustomer,
	stripEmTags,
	canAccessInternalWorkspace,
} from '../../../utils/DataAccessUtils';
import {
	getBuyerOrContractorOrClientDetailsFromSearch,
	getContractorOrWorkOrderIdBasedOnView,
	getWorkOrderLink,
} from '../../../utils/WorkOrderUtils';
import { UserNameWithEmail } from '../../name_component/user/UserNameWithEmail';
import RowChatInput from '../../row_chat_input/RowChatInput';
import { ROLE_TYPES } from '../../../utils/DataConstants';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
	dismissWorkOrderNoteStatusForBuyer,
	dismissWorkOrderNoteStatusForSupplier,
	dismissWorkOrderInternalNoteStatusForBuyer,
	dismissWorkOrderInternalNoteStatusForSupplier,
	dismissWorkOrderContractorNoteStatusForBuyer,
	dismissWorkOrderContractorNoteStatusForSupplier,
} from '../../../thunks/work_orders_thunks';
import { isInternalTech } from '../../../utils/AuthUtils';
import { openInternalWorkspace } from '../../../actions/internal_workspace_actions';
import { changeClientView } from '../../../actions/client_view_actions';
import NoteChannelTag, { NoteChannel } from './NoteChannelTag';
import { useNoteSender, useMentionSearch } from '../../../hooks/useNoteSending';
import { useNotesBroadcast } from '../../../hooks/useNotesBroadcast';
import { WEB_SOCKET_ENTITY_TYPES } from '../../../utils/WebSocketUtils';

const moment = require('moment');

interface ChannelNote {
	channel: NoteChannel;
	text: string;
	addedBy: string;
	addedAt: string;
	type: string;
	isResponsePending: boolean;
	isAwaitingResponseFromCurrentUser: boolean;
	isAwaitingResponse: boolean;
	isUnreadByCurrentUser: boolean;
	taggedUsers: any[];
	highlightedHtml?: string;
}

/**
 * Chip shell for the multi-channel notes preview. Encapsulates the one piece of
 * behavior that cannot be done with pure CSS: on hover the Reply button must
 * appear *inside* the pill without the pill changing width — the message text
 * has to absorb the Reply button's footprint and truncate further.
 *
 * Mechanic: Reply visibility is driven by React state (not `:hover`), so when
 * `mouseenter` fires the chip DOM is still at its at-rest width. We measure
 * that width, pin it via inline `style.width`, and then re-render with the
 * Reply button visible. The note-content row (flex-1 + truncate) shrinks
 * inside the pinned width and its text ellipses — exactly the requested UX.
 * When the pointer leaves, we clear both the hover state and the pinned width
 * in the same update so the pill reflows back to its natural content width.
 *
 * The shell intentionally does nothing while any chip is in reply mode
 * (`anyReplyActive`) or while this chip itself is the one being replied to —
 * in those states the pill switches to a vertical stack driven by its slot.
 */
interface HoverLockingChipShellProps {
	slotClassName: string;
	chipClassName: string;
	isReplyingToThisChip: boolean;
	anyReplyActive: boolean;
	onChipClick: (e: React.MouseEvent) => void;
	onReplyClick: () => void;
	replyComposer?: React.ReactNode;
	children: React.ReactNode;
}

const HoverLockingChipShell: React.FC<HoverLockingChipShellProps> = ({
	slotClassName,
	chipClassName,
	isReplyingToThisChip,
	anyReplyActive,
	onChipClick,
	onReplyClick,
	replyComposer,
	children,
}) => {
	const chipRef = useRef<HTMLDivElement>(null);
	const [hovered, setHovered] = useState(false);
	const [lockedWidth, setLockedWidth] = useState<number | null>(null);

	const handleMouseEnter = () => {
		if (isReplyingToThisChip || anyReplyActive) return;
		const node = chipRef.current;
		if (node) {
			// Only pin the width when the chip is already at its max-w (text being
			// truncated) — that chip can absorb Reply by truncating further. For
			// small chips where the text fully fits, pinning at the small natural
			// width forces Reply to overlap the timestamp/avatar; instead let the
			// chip grow to its slot's max-width.
			const textEl = node.querySelector('[data-chip-text-content]') as HTMLElement | null;
			const textAlreadyTruncated = textEl !== null && textEl.scrollWidth > textEl.clientWidth + 1;
			if (textAlreadyTruncated) {
				setLockedWidth(node.getBoundingClientRect().width);
			}
		}
		setHovered(true);
	};
	const handleMouseLeave = () => {
		if (!hovered && lockedWidth === null) return;
		setHovered(false);
		setLockedWidth(null);
	};

	const showReplyButton = hovered && !anyReplyActive && !isReplyingToThisChip;
	const widthStyle: React.CSSProperties | undefined =
		showReplyButton && lockedWidth !== null ? { width: lockedWidth } : undefined;

	return (
		<div className={`${slotClassName} min-w-0 overflow-hidden`}>
			<div
				ref={chipRef}
				style={widthStyle}
				className={chipClassName}
				onClick={onChipClick}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{children}
				{showReplyButton && (
					<span
						role="button"
						className="footnoteregular ml-2 inline-flex shrink-0 cursor-pointer flex-row items-center gap-1 rounded border border-solid border-grey-6 bg-white px-2 text-character-primary hover:border-character-primary"
						onClick={(e) => {
							stopDefaultEvents(e);
							onReplyClick();
						}}
					>
						<RollbackOutlined className="text-xs" />
						Reply
					</span>
				)}
				{isReplyingToThisChip && replyComposer}
			</div>
		</div>
	);
};

const findHighlightMatch = (noteText: string, highlightEntries: string[]): string | undefined => {
	if (!noteText || !highlightEntries || !highlightEntries.length) return undefined;
	// First pass: exact match
	for (let i = 0; i < highlightEntries.length; i++) {
		const plain = stripEmTags(highlightEntries[i]);
		if (plain === noteText) {
			return highlightEntries[i];
		}
	}
	// Second pass: noteText contains the highlight plain text (highlight is a truncated snippet)
	let bestMatch: string | undefined;
	let bestLen = 0;
	for (let j = 0; j < highlightEntries.length; j++) {
		const plainJ = stripEmTags(highlightEntries[j]);
		if (noteText.indexOf(plainJ) !== -1 && plainJ.length > bestLen) {
			bestLen = plainJ.length;
			bestMatch = highlightEntries[j];
		}
	}
	return bestMatch;
};

const getNoteDisplayText = (text, type) => {
	if (text) return text;
	if (type === 'image') return 'Sent a photo';
	if (type === 'video') return 'Sent a video';
	if (type === 'audio') return 'Sent a voice note';
	return '';
};

const getChannelNotes = (workOrder, highlight?: any): ChannelNote[] => {
	const notes: ChannelNote[] = [];
	const notesHighlights = nullSafeGetOrElse('notes', highlight, []);
	const internalHighlights = nullSafeGetOrElse('internalSpaces', highlight, []);
	const matchHighlight = (text: string | null | undefined, perFieldEntries: string[]) =>
		text ? findHighlightMatch(text, perFieldEntries) : undefined;

	const clientText = nullSafeGet('lastNote', workOrder);
	const clientType = nullSafeGet('lastNoteType', workOrder);
	if (clientText || clientType) {
		const displayText = getNoteDisplayText(clientText, clientType);
		if (displayText) {
			notes.push({
				channel: 'client',
				text: displayText,
				addedBy: nullSafeGetOrElse('lastNoteAddedBy', workOrder, ''),
				addedAt: nullSafeGetOrElse('lastNoteAddedAt', workOrder, ''),
				type: clientType || 'text',
				isResponsePending: !!nullSafeGet('isLastNoteResponsePending', workOrder),
				isAwaitingResponseFromCurrentUser: !!nullSafeGet(
					'isLastNoteAwaitingResponseFromCurrentUser',
					workOrder
				),
				isAwaitingResponse: !!nullSafeGet('isLastNoteAwaitingResponse', workOrder),
				isUnreadByCurrentUser: !!nullSafeGet('isLastNoteUnreadByCurrentUser', workOrder),
				taggedUsers: nullSafeGetOrElse('lastNoteTaggedUsers', workOrder, []),
				highlightedHtml: matchHighlight(clientText, notesHighlights),
			});
		}
	}

	const internalText = nullSafeGet('lastInternalNote', workOrder);
	const internalType = nullSafeGet('lastInternalNoteType', workOrder);
	if (internalText || internalType) {
		const displayText = getNoteDisplayText(internalText, internalType);
		if (displayText) {
			notes.push({
				channel: 'internal',
				text: displayText,
				addedBy: nullSafeGetOrElse('lastInternalNoteAddedBy', workOrder, ''),
				addedAt: nullSafeGetOrElse('lastInternalNoteAddedAt', workOrder, ''),
				type: internalType || 'text',
				isResponsePending: !!nullSafeGet('isLastInternalNoteResponsePending', workOrder),
				isAwaitingResponseFromCurrentUser: !!nullSafeGet(
					'isLastInternalNoteAwaitingResponseFromCurrentUser',
					workOrder
				),
				isAwaitingResponse: !!nullSafeGet('isLastInternalNoteAwaitingResponse', workOrder),
				isUnreadByCurrentUser: !!nullSafeGet('isLastInternalNoteUnreadByCurrentUser', workOrder),
				taggedUsers: nullSafeGetOrElse('lastInternalNoteTaggedUsers', workOrder, []),
				highlightedHtml: matchHighlight(internalText, internalHighlights),
			});
		}
	}

	const contractorText = nullSafeGet('lastContractorNote', workOrder);
	const contractorType = nullSafeGet('lastContractorNoteType', workOrder);
	if (contractorText || contractorType) {
		const displayText = getNoteDisplayText(contractorText, contractorType);
		if (displayText) {
			notes.push({
				channel: 'subcontractor',
				text: displayText,
				addedBy: nullSafeGetOrElse('lastContractorNoteAddedBy', workOrder, ''),
				addedAt: nullSafeGetOrElse('lastContractorNoteAddedAt', workOrder, ''),
				type: contractorType || 'text',
				isResponsePending: !!nullSafeGet('isLastContractorNoteResponsePending', workOrder),
				isAwaitingResponseFromCurrentUser: !!nullSafeGet(
					'isLastContractorNoteAwaitingResponseFromCurrentUser',
					workOrder
				),
				isAwaitingResponse: !!nullSafeGet('isLastContractorNoteAwaitingResponse', workOrder),
				isUnreadByCurrentUser: !!nullSafeGet('isLastContractorNoteUnreadByCurrentUser', workOrder),
				taggedUsers: nullSafeGetOrElse('lastContractorNoteTaggedUsers', workOrder, []),
				highlightedHtml: matchHighlight(contractorText, notesHighlights),
			});
		}
	}

	return notes;
};

/**
 * Renders the same status-slot chrome (message icon, or hourglass + "Awaiting response" /
 * "Waiting on you:" prefix) that the real chip renders, but without hover-only affordances
 * (dismiss X, unread dot) which are absolutely positioned and do not contribute to
 * `offsetWidth` anyway. Used exclusively by the off-screen measure strip so natural-width
 * reads equal the real chip's at-rest width.
 */
const renderNoteStatusSlotForMeasurement = (note: ChannelNote): JSX.Element => {
	if (note.isAwaitingResponseFromCurrentUser) {
		return (
			<span className="relative flex shrink-0 items-center gap-1">
				{note.isResponsePending ? (
					<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
						{'[>7d]'}
					</span>
				) : null}
				<span className="relative inline-block">
					<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
						<HourglassOutlined className="text-sm" />
					</span>
				</span>
				<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
					Waiting on you:
				</span>
			</span>
		);
	}
	if (note.isAwaitingResponse && (note.channel !== 'client' || !(note.taggedUsers || []).length)) {
		return (
			<span className="relative flex shrink-0 items-center gap-1">
				<span className="relative inline-flex items-center gap-1 whitespace-nowrap rounded-[2px] border border-solid border-transparent bg-transparent px-1">
					<span className="relative inline-block">
						<span className="bodybold flex flex-row items-center gap-1 text-character-primary">
							<HourglassOutlined className="text-sm" />
						</span>
					</span>
					<span className="bodybold flex flex-row items-center gap-1 text-character-primary">
						Awaiting response
						<span>:</span>
					</span>
				</span>
			</span>
		);
	}
	if (note.isAwaitingResponse) {
		return (
			<span className="relative flex shrink-0 items-center gap-1">
				<span className="relative inline-block">
					<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-character-primary">
						<HourglassOutlined className="text-sm" />
					</span>
				</span>
				<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-character-primary">
					Awaiting response:
				</span>
			</span>
		);
	}
	return (
		<span className="relative flex shrink-0 items-center gap-1">
			<span className="relative inline-block">
				<MessageOutlined className="text-grey-9" />
			</span>
		</span>
	);
};

interface NoteRowProps {
	note: ChannelNote;
	showTag: boolean;
}

const NoteRow: React.FC<NoteRowProps> = ({ note, showTag }) => {
	return (
		<div className="flex min-w-0 max-w-full flex-row items-center gap-2">
			{showTag && <NoteChannelTag channel={note.channel} />}
			<MessageOutlined className="shrink-0 text-grey-9" />
			<span
				className="footnoteregular min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-grey-9"
				title={note.text}
			>
				{note.text}
			</span>
			<span className="footnoteregular ml-1 shrink-0 text-grey-9">—</span>
			<span className="flex shrink-0 items-center">
				<UserNameWithEmail mode="avatar" email={note.addedBy} size="small" />
			</span>
			<span className="footnoteregular shrink-0 whitespace-nowrap text-grey-9">
				{' ∙ '}
				{note.addedAt ? moment(note.addedAt).format('hh:mm a') : ''}
			</span>
		</div>
	);
};

/**
 * Channel priority order used by the chip layout packer. Chips are rendered in this
 * order; adding a channel or reordering them is a single-line change here. The layout
 * algorithm itself has no hardcoded awareness of individual channels.
 */
const CHANNEL_DISPLAY_ORDER: readonly NoteChannel[] = ['client', 'internal', 'subcontractor'];
const getChannelDisplayIndex = (channel: NoteChannel): number => {
	const idx = CHANNEL_DISPLAY_ORDER.indexOf(channel);
	return idx === -1 ? CHANNEL_DISPLAY_ORDER.length : idx;
};

/** Hard cap on how many horizontal rows of chips we're willing to render. */
const MAX_CHIP_ROWS = 3;
/** Horizontal gap between chips in a row (must match `gap-x-4`). */
const CHIP_ROW_GAP_PX = 16;
/**
 * Minimum horizontal space we want to keep available for a chip being squeezed onto
 * the trailing end of a row. If less than this remains after the preceding chip(s),
 * the incoming chip is pushed to a new row instead of sharing at a sliver width.
 *
 * Sized so the trailing chip can comfortably show its channel tag, "Awaiting
 * response:" prefix, a few characters of message text, the avatar, and the
 * timestamp. Below this width, both chips end up heavily truncating side-by-side
 * (looks busy), so we'd rather wrap onto separate lines.
 */
const MIN_CHIP_SHARED_ROW_WIDTH_PX = 320;

/**
 * How a chip should be rendered within its row. Derived by the layout packer from
 * container width plus measured chip widths, consumed by the renderer to pick classes.
 *   - `alone-on-row`         : only chip on the row → content-sized, trailing empty.
 *   - `first-in-shared-row`  : leading chip when the row has multiple chips; capped
 *                              at 50% and `shrink-0`, so it can't monopolize the row.
 *   - `middle-in-shared-row` : chip between first and last in a row with ≥3 chips.
 *   - `last-in-shared-row`   : trailing chip; sits in a `flex-1` bay and takes the
 *                              row remainder (natural if short, truncate if long).
 */
type ChipSlotRole =
	| 'alone-on-row'
	| 'first-in-shared-row'
	| 'middle-in-shared-row'
	| 'last-in-shared-row';

interface ChipSlotPlacement {
	channel: NoteChannel;
	role: ChipSlotRole;
}

interface ChipLayoutInput {
	channel: NoteChannel;
	naturalWidth: number;
	/**
	 * Force this chip onto its own row regardless of fit. Used for chips that
	 * carry a status tag (Awaiting response / Waiting on you / [>7d]) — sharing
	 * a row leaves the hover Reply button no room and it overlaps the trailing
	 * timestamp. A solo row also lets the chip truncate to the full container
	 * width when content is long.
	 */
	forceAlone?: boolean;
}

interface ChipLayout {
	/** Filled rows, in render order. Length ≤ `maxRows`. */
	rows: ChipSlotPlacement[][];
	/** Chips that did not fit within `maxRows` — caller decides how to surface them. */
	overflowChannels: NoteChannel[];
}

/**
 * Pure, priority-preserving, N-row bin packer for channel-note chips.
 *
 * Rules (scale to any chip count, not just 3):
 *   1. Chips are consumed in input order — that order defines product priority, and
 *      the packer never reorders.
 *   2. A chip is placed on the current row at its natural width if it fits.
 *   3. If it doesn't fit at natural width but at least `minSharedRowWidthPx` remains
 *      after the gap, it is squeezed onto the row as the trailing chip (which will
 *      truncate into that remainder at render time).
 *   4. Otherwise the current row is finalized and the chip starts a new row.
 *   5. Once `maxRows` rows are filled, any remaining chips go into `overflowChannels`.
 *
 * Role assignment within a finalized row:
 *   - single chip on the row → `alone-on-row`
 *   - first / last chip of a multi-chip row → `first-in-shared-row` / `last-in-shared-row`
 *   - any chip between first and last → `middle-in-shared-row`
 */
const computeChipLayout = (
	chips: ChipLayoutInput[],
	containerWidthPx: number,
	maxRows: number,
	gapPx: number,
	minSharedRowWidthPx: number
): ChipLayout => {
	const rows: ChipSlotPlacement[][] = [];
	const overflowChannels: NoteChannel[] = [];
	if (chips.length === 0 || containerWidthPx < 1 || maxRows < 1) {
		for (let i = 0; i < chips.length; i++) overflowChannels.push(chips[i].channel);
		return { rows, overflowChannels };
	}

	let currentRow: ChipLayoutInput[] = [];
	let currentRowUsedWidth = 0;

	const finalizeRow = (): void => {
		if (currentRow.length === 0) return;
		const placements: ChipSlotPlacement[] = currentRow.map((chip, idx, arr) => {
			if (arr.length === 1) return { channel: chip.channel, role: 'alone-on-row' };
			if (idx === 0) return { channel: chip.channel, role: 'first-in-shared-row' };
			if (idx === arr.length - 1) return { channel: chip.channel, role: 'last-in-shared-row' };
			return { channel: chip.channel, role: 'middle-in-shared-row' };
		});
		rows.push(placements);
		currentRow = [];
		currentRowUsedWidth = 0;
	};

	for (let i = 0; i < chips.length; i++) {
		const chip = chips[i];
		if (rows.length >= maxRows && currentRow.length === 0) {
			overflowChannels.push(chip.channel);
			continue;
		}
		// Status-tag chips need their own row (see `forceAlone` doc on ChipLayoutInput).
		// Finalize whatever's already in flight, drop this chip alone, then move on.
		if (chip.forceAlone) {
			finalizeRow();
			if (rows.length >= maxRows) {
				overflowChannels.push(chip.channel);
				continue;
			}
			currentRow.push(chip);
			currentRowUsedWidth = containerWidthPx;
			finalizeRow();
			continue;
		}
		const gapBefore = currentRow.length > 0 ? gapPx : 0;
		const requiredWidthAtNatural = currentRowUsedWidth + gapBefore + chip.naturalWidth;
		if (requiredWidthAtNatural <= containerWidthPx) {
			currentRow.push(chip);
			currentRowUsedWidth = requiredWidthAtNatural;
			continue;
		}
		// Chip doesn't fit at natural width on the current row.
		if (currentRow.length === 0) {
			// Wider than the container; render alone on this row and truncate.
			currentRow.push(chip);
			currentRowUsedWidth = containerWidthPx;
			finalizeRow();
			continue;
		}
		const remainingAfterGap = containerWidthPx - currentRowUsedWidth - gapPx;
		if (remainingAfterGap >= minSharedRowWidthPx) {
			// Squeeze onto current row as trailing chip; it will truncate into the remainder.
			currentRow.push(chip);
			currentRowUsedWidth = containerWidthPx;
			finalizeRow();
			continue;
		}
		// Remainder too tight — finalize current row and start a new row with this chip.
		finalizeRow();
		if (rows.length >= maxRows) {
			overflowChannels.push(chip.channel);
			continue;
		}
		currentRow.push(chip);
		currentRowUsedWidth = chip.naturalWidth;
	}
	finalizeRow();
	return { rows, overflowChannels };
};

/** Structural equality for `ChipLayout`, used to avoid redundant `setState` calls. */
const chipLayoutsEqual = (a: ChipLayout | null, b: ChipLayout | null): boolean => {
	if (a === b) return true;
	if (!a || !b) return false;
	if (a.rows.length !== b.rows.length) return false;
	if (a.overflowChannels.length !== b.overflowChannels.length) return false;
	for (let i = 0; i < a.rows.length; i++) {
		if (a.rows[i].length !== b.rows[i].length) return false;
		for (let j = 0; j < a.rows[i].length; j++) {
			if (
				a.rows[i][j].channel !== b.rows[i][j].channel ||
				a.rows[i][j].role !== b.rows[i][j].role
			) {
				return false;
			}
		}
	}
	for (let i = 0; i < a.overflowChannels.length; i++) {
		if (a.overflowChannels[i] !== b.overflowChannels[i]) return false;
	}
	return true;
};

interface ChannelReplyInputProps {
	workOrder: any;
	channel?: NoteChannel;
	onNoteSent: () => void;
	onCancel: () => void;
}

// Per-channel WebSocket entity-type config for the inline reply broadcast. Keeps
// listeners on the WO detail page (regular notes / internal notes / contractor
// notes) wired up so they refresh / toast when a reply lands from the row.
const getBroadcastEntityTypesForChannel = (
	channel?: NoteChannel
): { entityType: string; newNoteEntityType: string } => {
	if (channel === 'internal') {
		return {
			entityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_INTERNAL_NOTES,
			newNoteEntityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_INTERNAL_NOTES,
		};
	}
	return {
		entityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_NOTES,
		newNoteEntityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER,
	};
};

const ChannelReplyInput: React.FC<ChannelReplyInputProps> = ({
	workOrder,
	channel,
	onNoteSent,
	onCancel,
}) => {
	const { sendNote, currentUser, workOrderId, noteId } = useNoteSender(workOrder, channel);
	const { onSearch } = useMentionSearch(workOrder, channel);
	const { entityType, newNoteEntityType } = getBroadcastEntityTypesForChannel(channel);
	const { onSendNote: broadcastNewNote } = useNotesBroadcast({
		entityIdName: 'workOrderId',
		entityId: noteId,
		entityType,
		newNoteEntityType,
		currentUser,
	});
	return (
		<RowChatInput
			sendNote={sendNote}
			onMentionSearch={onSearch}
			currentUser={currentUser}
			entityId={noteId}
			entityLabel={`WO #${workOrderId}`}
			autoFocus
			onWsSendNote={broadcastNewNote}
			onNoteSent={onNoteSent}
			onCancel={onCancel}
		/>
	);
};

interface WorkOrderRowNotePreviewProps {
	workOrder: any;
	userType: string;
	targetCollectionName: string | null;
	currentUser: any;
	isClientView: boolean;
	refreshWOFromSearch: (id: number) => void;
	openWorkspace: (activeTab?: string) => void;
	switchToSubcontractorView: () => void;
	refreshTable?: (location?) => void;
	dismissNoteStatus: (params: {
		workOrderId: number;
		noteText: string;
		noteAddedAtMillis: number;
		channel?: NoteChannel;
	}) => Promise<any>;
}

const WorkOrderRowNotePreview: React.FC<WorkOrderRowNotePreviewProps> = ({
	workOrder,
	userType,
	targetCollectionName,
	currentUser,
	isClientView,
	refreshWOFromSearch,
	openWorkspace,
	switchToSubcontractorView,
	refreshTable,
	dismissNoteStatus,
}) => {
	const location = useLocation();
	const history = useHistory();
	const [dismissing, setDismissing] = useState(false);
	const [dismissingChannel, setDismissingChannel] = useState<NoteChannel | null>(null);
	const [replyChannel, setReplyChannel] = useState<NoteChannel | null>(null);

	const showMultiChannel = canAccessInternalWorkspace(currentUser, isClientView);

	const lastNote = nullSafeGet('lastNote', workOrder);
	const lastNoteUnread = nullSafeGet('isLastNoteUnreadByCurrentUser', workOrder);

	const channelNotes = useMemo(() => {
		if (!showMultiChannel) {
			return [];
		}
		const highlight = nullSafeGet('highlight', workOrder);
		const notes = getChannelNotes(workOrder, highlight);
		// Match the parent's "is the card in search-highlight mode?" check: highlight
		// is only meaningful when it has keys. An empty `{}` from the API must not
		// trigger filtering — otherwise non-search rows with populated last notes
		// would render as empty.
		// Only filter against highlights when the highlight bag contains note-channel
		// entries. If the search matched on non-note fields like `id` or `title` (no
		// `notes` / `internalSpaces` / `contractorNotes` keys), filtering would strip
		// every chip and leave the card noteless even though there's nothing noisy to
		// hide — fall back to the unfiltered list in that case.
		const hasNoteHighlightEntries =
			!!highlight &&
			(nullSafeGetOrElse('notes', highlight, []).length > 0 ||
				nullSafeGetOrElse('internalSpaces', highlight, []).length > 0 ||
				nullSafeGetOrElse('contractorNotes', highlight, []).length > 0);
		const filtered = hasNoteHighlightEntries
			? notes.filter((note) => !!note.highlightedHtml)
			: notes;
		// Enforce the channel display order here so downstream code (layout packer,
		// renderer) never has to care which channel is at which index.
		return [...filtered].sort(
			(a, b) => getChannelDisplayIndex(a.channel) - getChannelDisplayIndex(b.channel)
		);
	}, [showMultiChannel, workOrder]);

	const channelNotesLayoutContainerRef = useRef<HTMLDivElement>(null);
	/**
	 * Stable refs for the off-screen natural-width samples (one per channel). Using `useRef`
	 * (vs. inline `ref={(el) => ...}`) is load-bearing here: inline ref callbacks are fired
	 * twice on every render (null → element), and if the layout callback runs during the
	 * null window it would read 0 widths and briefly pick the wrong mode (flicker).
	 */
	const clientNaturalWidthSampleRef = useRef<HTMLDivElement | null>(null);
	const internalNaturalWidthSampleRef = useRef<HTMLDivElement | null>(null);
	const subcontractorNaturalWidthSampleRef = useRef<HTMLDivElement | null>(null);
	const naturalWidthSampleRefForChannel = (
		channel: NoteChannel
	): React.MutableRefObject<HTMLDivElement | null> => {
		if (channel === 'client') return clientNaturalWidthSampleRef;
		if (channel === 'internal') return internalNaturalWidthSampleRef;
		return subcontractorNaturalWidthSampleRef;
	};
	/**
	 * Computed by `updateChipLayoutFromWidths` (run from a layout effect + ResizeObserver).
	 * `null` on the very first render — the layout effect fires synchronously after that
	 * commit and before the browser paints, so the null state is never actually visible.
	 */
	const [chipLayout, setChipLayout] = useState<ChipLayout | null>(null);

	/**
	 * Last container width we actually packed against. Used to suppress `ResizeObserver`
	 * callbacks triggered by height-only changes — crucial because a chip entering reply
	 * mode grows the row vertically, and if we recomputed on every size event the chip
	 * could oscillate out of the layout (page gets tall → viewport scrollbar appears →
	 * container narrows a few px → packer can no longer fit the trailing chip into the
	 * remaining row space → chip drops to overflow → composer unmounts → page shortens →
	 * scrollbar gone → container widens → chip comes back → loop). The packer is a pure
	 * function of widths, so gating recomputes on actual width deltas breaks that cycle.
	 */
	const lastPackedContainerWidthRef = useRef<number | null>(null);

	const updateChipLayoutFromWidths = useCallback(() => {
		const layoutRoot = channelNotesLayoutContainerRef.current;
		if (!layoutRoot || !showMultiChannel || channelNotes.length === 0) {
			return;
		}
		const containerWidthPx = layoutRoot.getBoundingClientRect().width;
		if (containerWidthPx < 1) {
			return;
		}
		// In search-highlight mode the chips carry `<em>` markup that bloats their
		// natural width and the row often has multiple unconsumed-highlight chips
		// rendered alongside it — packing channel chips onto the same row makes the
		// layout busy and frequently overflows. Force one chip per row whenever the
		// card is in highlight mode so each note reads cleanly on its own line.
		const highlight = nullSafeGet('highlight', workOrder);
		// Only force the one-chip-per-row layout when the highlight bag actually has
		// note-channel entries that the chips will carry `<em>` markup for. Non-note
		// matches (e.g. `id` / `title`) leave the chips at their normal widths, so we
		// can keep the regular packed layout.
		const isSearchHighlightMode =
			!!highlight &&
			(nullSafeGetOrElse('notes', highlight, []).length > 0 ||
				nullSafeGetOrElse('internalSpaces', highlight, []).length > 0 ||
				nullSafeGetOrElse('contractorNotes', highlight, []).length > 0);
		if (isSearchHighlightMode) {
			const verticalLayout: ChipLayout = {
				rows: channelNotes.map((note) => [{ channel: note.channel, role: 'alone-on-row' }]),
				overflowChannels: [],
			};
			lastPackedContainerWidthRef.current = containerWidthPx;
			setChipLayout((prev) => (chipLayoutsEqual(prev, verticalLayout) ? prev : verticalLayout));
			return;
		}
		const inputs: ChipLayoutInput[] = [];
		for (let i = 0; i < channelNotes.length; i++) {
			const note = channelNotes[i];
			const sampleEl = naturalWidthSampleRefForChannel(note.channel).current;
			if (!sampleEl) {
				// Bail until every sample element is mounted — avoids reading 0 widths mid-render.
				return;
			}
			// Any chip carrying a status tag (Waiting on you / Awaiting response / [>7d])
			// gets its own row so the hover Reply button has room to render and the
			// timestamp doesn't get clipped.
			const hasStatusTag =
				!!note.isResponsePending ||
				!!note.isAwaitingResponseFromCurrentUser ||
				!!note.isAwaitingResponse;
			inputs.push({
				channel: note.channel,
				naturalWidth: sampleEl.offsetWidth,
				forceAlone: hasStatusTag,
			});
		}
		const nextLayout = computeChipLayout(
			inputs,
			containerWidthPx,
			MAX_CHIP_ROWS,
			CHIP_ROW_GAP_PX,
			MIN_CHIP_SHARED_ROW_WIDTH_PX
		);
		lastPackedContainerWidthRef.current = containerWidthPx;
		setChipLayout((prev) => (chipLayoutsEqual(prev, nextLayout) ? prev : nextLayout));
	}, [showMultiChannel, channelNotes, workOrder]);

	useLayoutEffect(() => {
		if (!showMultiChannel || channelNotes.length === 0) {
			return undefined;
		}
		updateChipLayoutFromWidths();
		const layoutRoot = channelNotesLayoutContainerRef.current;
		if (!layoutRoot) {
			return undefined;
		}
		let resizeAnimationFrame = 0;
		// Threshold is sub-pixel; anything larger is a real width change (including a
		// scrollbar appearing / disappearing). Height-only events come through with
		// `contentRect.width` matching the last packed width exactly and get suppressed.
		const WIDTH_CHANGE_EPSILON_PX = 0.5;
		const resizeObserver = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (!entry) return;
			const observedWidthPx = entry.contentRect.width;
			const lastWidthPx = lastPackedContainerWidthRef.current;
			if (
				lastWidthPx !== null &&
				Math.abs(observedWidthPx - lastWidthPx) < WIDTH_CHANGE_EPSILON_PX
			) {
				return;
			}
			window.cancelAnimationFrame(resizeAnimationFrame);
			resizeAnimationFrame = window.requestAnimationFrame(() => {
				updateChipLayoutFromWidths();
			});
		});
		resizeObserver.observe(layoutRoot);
		return () => {
			window.cancelAnimationFrame(resizeAnimationFrame);
			resizeObserver.disconnect();
		};
	}, [showMultiChannel, channelNotes, updateChipLayoutFromWidths]);

	const handleNoteClick = (e, channel?: NoteChannel) => {
		stopDefaultEvents(e);
		if (channel === 'internal') {
			openWorkspace('notes');
			history.push(
				getWorkOrderLink({
					userType,
					tab: 'internalSpace',
					workOrder,
					targetCollectionName,
					location,
					BACK_TEXT: 'Back to Work Orders',
				})
			);
		} else if (channel === 'subcontractor') {
			switchToSubcontractorView();
			history.push(
				getWorkOrderLink({
					userType,
					tab: 'contractor_notes',
					workOrder,
					targetCollectionName,
					location,
					BACK_TEXT: 'Back to Work Orders',
				})
			);
		} else {
			history.push(
				getWorkOrderLink({
					userType,
					tab: 'notes',
					workOrder,
					targetCollectionName,
					location,
					BACK_TEXT: 'Back to Work Orders',
				})
			);
		}
	};

	const renderReplyInput = (channel?: NoteChannel) => (
		<div
			onClick={(e) => stopDefaultEvents(e)}
			onKeyDown={(e) => {
				if (e.key === 'Escape') {
					setReplyChannel(null);
				}
			}}
			className="w-full"
		>
			<ChannelReplyInput
				workOrder={workOrder}
				channel={channel}
				onNoteSent={() => {
					setReplyChannel(null);
					if (refreshTable) {
						refreshTable(location);
					} else {
						const id = parseInt(nullSafeGet('id', workOrder), 10);
						if (id) refreshWOFromSearch(id);
					}
				}}
				onCancel={() => setReplyChannel(null)}
			/>
		</div>
	);

	const handleDismissNoteStatus = (e, channel: NoteChannel = 'client') => {
		stopDefaultEvents(e);
		const woId = parseInt(nullSafeGet('id', workOrder), 10);
		const textKey =
			channel === 'internal'
				? 'lastInternalNote'
				: channel === 'subcontractor'
				? 'lastContractorNote'
				: 'lastNote';
		const addedAtKey =
			channel === 'internal'
				? 'lastInternalNoteAddedAt'
				: channel === 'subcontractor'
				? 'lastContractorNoteAddedAt'
				: 'lastNoteAddedAt';
		const noteText = nullSafeGet(textKey, workOrder);
		const noteAddedAtMillis = nullSafeGet(addedAtKey, workOrder);
		// Subcontractor notes live on the contractor work order, not the parent
		// WO — the dismiss endpoint expects the contractor work-order id for that
		// channel. Search-row workOrders carry the contractor WO ids inside
		// `subContractedDetails`, scoped per supplier facility; use the same
		// lookup `useNoteSender` uses for sending into that channel.
		let dismissId = woId;
		if (channel === 'subcontractor') {
			const subDetails = nullSafeGetOrElse('subContractedDetails', workOrder, []);
			const supplierFacilityId = nullSafeGet('currentUserSupplierFacilityId', workOrder);
			const match = subDetails.find(
				(detail) => detail.subContractedFromSupplierFacilityId == supplierFacilityId
			);
			const matchedId = parseInt(nullSafeGet('id', match), 10);
			dismissId =
				matchedId ||
				parseInt(getContractorOrWorkOrderIdBasedOnView(workOrder, isClientView), 10) ||
				woId;
		}
		setDismissing(true);
		setDismissingChannel(channel);
		dismissNoteStatus({ workOrderId: dismissId, noteText, noteAddedAtMillis, channel })
			.then(() => {
				refreshWOFromSearch(woId);
				const hasNoteFilter = location.search && location.search.indexOf('noteStatus') !== -1;
				if (hasNoteFilter && refreshTable) {
					refreshTable(location);
				}
			})
			.catch(() => {
				message.error('Failed to dismiss note status. Please try again.');
			})
			.finally(() => {
				setDismissing(false);
				setDismissingChannel(null);
			});
	};

	const renderMultiChannelNoteChip = (note: ChannelNote, slotClassName: string) => {
		/*
		 * Reply mode: the chip switches to a vertical stack so the reply composer
		 * renders as a second row *inside* the pill (same left border, background,
		 * and rounded corners). The chip's width is still driven by its slot — the
		 * composer is bounded by the pill's width and never escapes it.
		 *
		 * At-rest / hover: handled by `HoverLockingChipShell`, which keeps the
		 * pill's width fixed on hover so the Reply button appears without growing
		 * the pill — the message text truncates further to make room.
		 */
		const isReplyingToThisChip = replyChannel === note.channel;
		const chipClassName = `group/note flex min-h-8 min-w-0 cursor-pointer overflow-hidden rounded-l rounded-r-sm border-0 border-l-2 border-solid border-l-grey-8 px-2 py-1 ${
			isReplyingToThisChip
				? 'flex-col items-stretch gap-1 bg-grey-4'
				: 'flex-row flex-nowrap items-center bg-grey-3 hover:bg-grey-5 hover:shadow-sm'
		}`;
		return (
			<HoverLockingChipShell
				key={note.channel}
				slotClassName={slotClassName}
				chipClassName={chipClassName}
				isReplyingToThisChip={isReplyingToThisChip}
				anyReplyActive={!!replyChannel}
				onChipClick={(e) => handleNoteClick(e, note.channel)}
				onReplyClick={() => setReplyChannel(note.channel)}
				replyComposer={
					isReplyingToThisChip ? (
						<div className="w-full min-w-0">{renderReplyInput(note.channel)}</div>
					) : null
				}
			>
				<div className="flex w-full min-w-0 max-w-full flex-row flex-nowrap items-center gap-2">
					<NoteChannelTag channel={note.channel} />
					<span className="relative flex shrink-0 items-center gap-1">
						{note.isResponsePending ? (
							<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
								{'[>7d]'}
							</span>
						) : null}
						{note.isAwaitingResponseFromCurrentUser ? (
							<>
								<span className="relative inline-block">
									<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
										<HourglassOutlined className="text-sm" />
									</span>
									{note.isUnreadByCurrentUser && (
										<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
									)}
								</span>
								<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
									Waiting on you:
								</span>
							</>
						) : note.isAwaitingResponse &&
						  (note.channel !== 'client' || !(note.taggedUsers || []).length) ? (
							<span className="relative inline-flex items-center gap-1 whitespace-nowrap rounded-[2px] border border-solid border-transparent bg-transparent px-1 transition-colors group-hover/note:border-grey-5 group-hover/note:bg-white">
								<span className="relative inline-block">
									<span className="bodybold flex flex-row items-center gap-1 text-character-primary">
										<HourglassOutlined className="text-sm" />
									</span>
									{note.isUnreadByCurrentUser && (
										<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
									)}
								</span>
								<span className="bodybold flex flex-row items-center gap-1 text-character-primary">
									Awaiting response
									<span className="opacity-100 group-hover/note:opacity-0">:</span>
								</span>
								<span
									role="button"
									className={`absolute -right-[7px] -top-[5px] h-[15px] w-[15px] cursor-pointer items-center justify-center rounded-full border border-solid border-grey-5 bg-white ${
										dismissing && dismissingChannel === note.channel
											? 'flex'
											: 'hidden group-hover/note:flex'
									}`}
									onClick={
										dismissing ? stopDefaultEvents : (e) => handleDismissNoteStatus(e, note.channel)
									}
								>
									{dismissing && dismissingChannel === note.channel ? (
										<LoadingOutlined
											className="!text-black"
											style={{
												fontSize: 6,
												width: 6,
												height: 6,
											}}
										/>
									) : (
										<CloseOutlined
											className="!text-black"
											style={{
												fontSize: 6,
												width: 6,
												height: 6,
											}}
										/>
									)}
								</span>
							</span>
						) : note.isAwaitingResponse ? (
							<>
								<span className="relative inline-block">
									<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-character-primary">
										<HourglassOutlined className="text-sm" />
									</span>
									{note.isUnreadByCurrentUser && (
										<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
									)}
								</span>
								<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-character-primary">
									Awaiting response:
								</span>
							</>
						) : (
							<span className="relative inline-block">
								<MessageOutlined className="text-grey-9" />
								{note.isUnreadByCurrentUser && (
									<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
								)}
							</span>
						)}
					</span>
					{note.highlightedHtml ? (
						<span
							data-chip-text-content=""
							className="highlights footnoteregular min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-grey-9"
							title={note.text}
							dangerouslySetInnerHTML={{ __html: note.highlightedHtml }}
						/>
					) : (
						<span
							data-chip-text-content=""
							className="footnoteregular min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-grey-9"
							title={note.text}
						>
							{note.text}
						</span>
					)}
					<span className="footnoteregular ml-1 shrink-0 text-grey-9">-</span>
					<span className="flex shrink-0 items-center">
						<UserNameWithEmail mode="avatar" email={note.addedBy} size="small" />
					</span>
					<span className="footnoteregular shrink-0 whitespace-nowrap text-grey-9">
						{' ∙ '}
						{note.addedAt ? moment(note.addedAt).format('hh:mm a') : ''}
					</span>
				</div>
			</HoverLockingChipShell>
		);
	};

	const renderMultiChannelNoteLayout = () => {
		if (channelNotes.length === 0) {
			return null;
		}

		const noteByChannel: Partial<Record<NoteChannel, ChannelNote>> = {};
		for (let i = 0; i < channelNotes.length; i++) {
			noteByChannel[channelNotes[i].channel] = channelNotes[i];
		}

		/*
		 * Off-screen natural-width samples. Must structurally mirror the real chip's at-rest
		 * DOM (same status-slot chrome, separator, avatar, timestamp) so `offsetWidth` matches
		 * the rendered chip width. Anything hover-only or absolutely positioned (dismiss X,
		 * unread dot, Reply button) is omitted — it doesn't contribute to at-rest width and
		 * including it would distort the sample.
		 */
		const measureStrip = (
			<div
				className="pointer-events-none fixed -left-[10000px] top-0 flex w-max flex-row flex-nowrap gap-x-4 opacity-0"
				style={{ visibility: 'hidden' }}
				aria-hidden="true"
			>
				{channelNotes.map((note) => (
					<div
						key={`measure-${note.channel}`}
						ref={naturalWidthSampleRefForChannel(note.channel)}
						className="flex min-h-8 shrink-0 flex-row flex-nowrap items-center gap-2 whitespace-nowrap rounded-l rounded-r-sm border-0 border-l-2 border-solid border-l-grey-8 bg-grey-3 px-2 py-1"
					>
						<NoteChannelTag channel={note.channel} />
						{renderNoteStatusSlotForMeasurement(note)}
						{note.highlightedHtml ? (
							// Mirror the rendered chip's `.highlights em` styling (padding around
							// each `<em>` token) so measured `offsetWidth` matches the actual paint
							// width. Without this, the packer underestimates chip widths whenever a
							// chip carries highlights and the trailing chip overflows its row.
							<span
								className="highlights footnoteregular text-grey-9"
								dangerouslySetInnerHTML={{ __html: note.highlightedHtml }}
							/>
						) : (
							<span className="footnoteregular text-grey-9">{note.text}</span>
						)}
						<span className="footnoteregular ml-1 shrink-0 text-grey-9">-</span>
						<span className="flex shrink-0 items-center">
							<UserNameWithEmail mode="avatar" email={note.addedBy} size="small" />
						</span>
						<span className="footnoteregular shrink-0 whitespace-nowrap text-grey-9">
							{' ∙ '}
							{note.addedAt ? moment(note.addedAt).format('hh:mm a') : ''}
						</span>
					</div>
				))}
			</div>
		);

		if (chipLayout === null) {
			// Pre-measurement render. The layout effect fires synchronously after this
			// commit and before the browser paints, so the user never actually sees this.
			// Rendering the measure strip is still required so the effect has DOM to read.
			return <>{measureStrip}</>;
		}

		/*
		 * Role → Tailwind class translation. The packer decides WHERE each chip lives
		 * (which row, which slot); this function decides HOW a slot is sized.
		 *
		 *   - `last-in-shared-row`  → `flex-1` bay that claims the row's remainder, with
		 *     an inner `w-fit max-w-full` wrapper (CSS `min(max-content, available)`).
		 *     Short chip stays at content width (empty space sits in the bay, not inside
		 *     the pill). Long chip stretches to the full bay width and truncates.
		 *   - `first-in-shared-row` → content-sized and `shrink-0`, capped at 50% so it
		 *     can never monopolize the row. Forces the trailing slot to absorb overflow.
		 *   - `middle-in-shared-row` / `alone-on-row` → content-sized, capped at 100%.
		 *     Any trailing space stays outside the pill, not as grey inside it.
		 */
		const renderChipSlot = (placement: ChipSlotPlacement): JSX.Element | null => {
			const note = noteByChannel[placement.channel];
			if (!note) return null;
			// Replying chip: stretch to the full row width so the composer inside the pill
			// spans the row (rather than being bounded by the chip's packed slot width).
			// The layout transform below guarantees this chip is alone on its row.
			if (replyChannel && placement.channel === replyChannel) {
				return (
					<div key={placement.channel} className="w-full min-w-0 max-w-full">
						{renderMultiChannelNoteChip(note, 'w-full max-w-full')}
					</div>
				);
			}
			switch (placement.role) {
				case 'last-in-shared-row':
					// `flex-1` bay claims the row's remainder; inner `w-fit max-w-full`
					// keeps the chip content-sized when short (empty space sits in the
					// bay, not as grey inside the pill) but lets it stretch to the full
					// bay width and truncate when content overflows.
					return (
						<div key={placement.channel} className="flex min-w-0 flex-1 items-center">
							<div className="w-fit min-w-0 max-w-full">
								{renderMultiChannelNoteChip(note, 'max-w-full')}
							</div>
						</div>
					);
				case 'first-in-shared-row':
					// Content-sized and capped at 50% so it can never monopolize the row,
					// forcing the trailing slot to absorb overflow.
					return (
						<div key={placement.channel} className="w-max min-w-0 max-w-[50%] shrink-0">
							{renderMultiChannelNoteChip(note, 'max-w-full')}
						</div>
					);
				case 'middle-in-shared-row':
				case 'alone-on-row':
				default:
					return (
						<div key={placement.channel} className="w-max min-w-0 max-w-full">
							{renderMultiChannelNoteChip(note, 'max-w-full')}
						</div>
					);
			}
		};

		// When a chip enters reply mode, split its row so the replying chip sits alone
		// (full-width, per renderChipSlot above) and any siblings from its original row
		// drop into a fresh row just below — preserving their relative order and roles.
		const assignRoleInRow = (
			placement: ChipSlotPlacement,
			indexInRow: number,
			rowLength: number
		): ChipSlotPlacement => {
			if (rowLength === 1) return { channel: placement.channel, role: 'alone-on-row' };
			if (indexInRow === 0) return { channel: placement.channel, role: 'first-in-shared-row' };
			if (indexInRow === rowLength - 1)
				return { channel: placement.channel, role: 'last-in-shared-row' };
			return { channel: placement.channel, role: 'middle-in-shared-row' };
		};

		const splitRowAroundReplyingChip = (
			originalRow: ChipSlotPlacement[]
		): ChipSlotPlacement[][] => {
			const replyingChipIndex = originalRow.findIndex(
				(placement) => placement.channel === replyChannel
			);
			if (replyingChipIndex === -1) return [originalRow];

			const replyingChipRow: ChipSlotPlacement[] = [
				{ channel: replyChannel as NoteChannel, role: 'alone-on-row' },
			];
			const precedingSiblings = originalRow.slice(0, replyingChipIndex);
			const followingSiblings = originalRow.slice(replyingChipIndex + 1);

			const rebalanceRow = (siblings: ChipSlotPlacement[]): ChipSlotPlacement[] =>
				siblings.map((placement, index) => assignRoleInRow(placement, index, siblings.length));

			const result: ChipSlotPlacement[][] = [];
			if (precedingSiblings.length > 0) result.push(rebalanceRow(precedingSiblings));
			result.push(replyingChipRow);
			if (followingSiblings.length > 0) result.push(rebalanceRow(followingSiblings));
			return result;
		};

		const rowsToRender: ChipSlotPlacement[][] = replyChannel
			? chipLayout.rows.reduce<ChipSlotPlacement[][]>(
					(accumulatedRows, row) => accumulatedRows.concat(splitRowAroundReplyingChip(row)),
					[]
			  )
			: chipLayout.rows;

		return (
			<>
				{measureStrip}
				<div className="flex w-full min-w-0 max-w-full flex-col gap-y-4">
					{rowsToRender.map((row, rowIdx) => (
						/*
						 * `items-start` top-anchors every slot. Without it, when one pill enters
						 * reply mode it grows tall and the row's default `align-items: stretch`
						 * stretches the sibling slots to the same height — which vertically
						 * centers the last-in-shared-row bay's chip (that bay is flex + items-center)
						 * so it floats in the middle of the tall reply pill. Top-aligning at the
						 * row level keeps every sibling anchored to the top regardless of which
						 * chip is expanded.
						 */
						<div
							key={rowIdx}
							className="flex w-full min-w-0 max-w-full flex-row flex-nowrap items-start gap-x-4"
						>
							{row.map(renderChipSlot)}
						</div>
					))}
				</div>
			</>
		);
	};

	// Multi-channel mode: show all note types with tags
	if (showMultiChannel) {
		if (channelNotes.length === 0) {
			// When the card is in search-highlight mode and the highlight contains
			// note-channel entries, the parent renders those as unconsumed chips
			// below us — showing Send Note here would sit above real chips and look
			// like an empty-state when it isn't. Skip the Send Note in that case.
			const highlight = nullSafeGet('highlight', workOrder);
			const hasUnconsumedNoteHighlights =
				nullSafeGetOrElse('notes', highlight, []).length > 0 ||
				nullSafeGetOrElse('internalSpaces', highlight, []).length > 0;
			if (hasUnconsumedNoteHighlights) {
				return null;
			}
			return (
				<div className="rowTertiary">
					{!replyChannel && (
						<div className="hidden min-h-8 items-center group-hover:flex">
							<span
								role="button"
								className="footnoteregular inline-flex cursor-pointer items-center gap-1 rounded border border-solid border-grey-6 bg-white px-2 text-character-primary hover:border-character-primary"
								onClick={(e) => {
									stopDefaultEvents(e);
									setReplyChannel('client');
								}}
							>
								<MessageOutlined className="text-xs" />
								Send Note
							</span>
						</div>
					)}
					{replyChannel && renderReplyInput(replyChannel)}
				</div>
			);
		}

		// When at least one channel has a note but the client channel doesn't, we still
		// want a way for the user to send a client note — hover reveals a Send Note button
		// below the existing chips (or the composer, if the user has clicked it).
		const clientNoteMissing = !channelNotes.some((n) => n.channel === 'client');

		return (
			<div className="rowTertiary">
				{/*
				 * `.rowTertiary` (in rowDisplay.less) is `display: flex`, so this div must
				 * grow into the available row width. Without `flex-1 w-full` it would be
				 * content-sized, which would make `getBoundingClientRect().width` report the
				 * content width instead of the available width — and every 3-note decision
				 * ends up picking a 2-row layout even when all three chips would fit.
				 *
				 * The reply composer is rendered *inside* the chip it belongs to (see
				 * `renderMultiChannelNoteChip`) so it stays bounded by that pill's width.
				 */}
				<div
					ref={channelNotesLayoutContainerRef}
					className="relative w-full min-w-0 max-w-full flex-1"
				>
					{renderMultiChannelNoteLayout()}
					{clientNoteMissing && replyChannel === 'client' && (
						<div className="mt-4 w-full">{renderReplyInput('client')}</div>
					)}
					{clientNoteMissing && !replyChannel && (
						<div className="mt-2 hidden min-h-8 items-center group-hover:flex">
							<span
								role="button"
								className="footnoteregular inline-flex cursor-pointer items-center gap-1 rounded border border-solid border-grey-6 bg-white px-2 text-character-primary hover:border-character-primary"
								onClick={(e) => {
									stopDefaultEvents(e);
									setReplyChannel('client');
								}}
							>
								<MessageOutlined className="text-xs" />
								Send Note
							</span>
						</div>
					)}
				</div>
			</div>
		);
	}

	// Single-channel mode (original behavior): show only lastNote without tags
	if (!lastNote) {
		return (
			<div className="rowTertiary">
				{!replyChannel && (
					<div className="flex max-h-0 items-center overflow-hidden opacity-0 transition-all duration-200 ease-out group-hover:max-h-8 group-hover:opacity-100">
						<span
							role="button"
							className="footnoteregular inline-flex cursor-pointer items-center gap-1 rounded border border-solid border-grey-6 bg-white px-2 text-character-primary hover:border-character-primary"
							onClick={(e) => {
								stopDefaultEvents(e);
								setReplyChannel('client');
							}}
						>
							<MessageOutlined className="text-xs" />
							Send Note
						</span>
					</div>
				)}
				{replyChannel && renderReplyInput()}
			</div>
		);
	}

	return (
		<div className="rowTertiary">
			<div
				className={`group flex min-h-8 min-w-0 max-w-full cursor-pointer flex-col items-start gap-1 overflow-hidden rounded-l rounded-r-sm border-0 border-l-2 border-solid border-l-grey-8 px-2 py-1 ${
					replyChannel
						? 'w-full bg-grey-4'
						: 'justify-center bg-grey-3 hover:bg-grey-5 hover:shadow-sm'
				} `}
			>
				<div className="group/notePreviewBar w-full min-w-0 max-w-full">
					<div
						onClick={(e) => {
							stopDefaultEvents(e);
							history.push(
								getWorkOrderLink({
									userType,
									tab: 'notes',
									workOrder,
									targetCollectionName,
									location,
									BACK_TEXT: 'Back to Work Orders',
								})
							);
						}}
						className="flex min-w-0 max-w-full cursor-pointer flex-row items-center gap-2"
					>
						<span className="relative flex shrink-0 items-center gap-1">
							{nullSafeGet('isLastNoteResponsePending', workOrder) ? (
								<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
									{'[>7d]'}
								</span>
							) : null}
							{nullSafeGet('isLastNoteAwaitingResponseFromCurrentUser', workOrder) ? (
								<>
									<span className="relative inline-block">
										<span
											className={`bodybold flex flex-row items-center gap-1 whitespace-nowrap ${
												nullSafeGet('isLastNoteAwaitingResponseFromCurrentUser', workOrder)
													? 'text-sunsetOrange-8'
													: 'text-character-primary'
											}`}
										>
											<HourglassOutlined className="text-sm" />
										</span>
										{lastNoteUnread && (
											<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
										)}
									</span>
									<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-sunsetOrange-8">
										Waiting on you:
									</span>
								</>
							) : nullSafeGet('isLastNoteAwaitingResponse', workOrder) &&
							  !(nullSafeGet('lastNoteTaggedUsers', workOrder) || []).length ? (
								<span className="relative inline-flex items-center gap-1 whitespace-nowrap rounded-[2px] border border-solid border-transparent bg-transparent px-1 transition-colors group-hover/notePreviewBar:border-grey-5 group-hover/notePreviewBar:bg-white">
									<span className="relative inline-block">
										<span className="bodybold flex flex-row items-center gap-1 text-character-primary">
											<HourglassOutlined className="text-sm" />
										</span>
										{lastNoteUnread && (
											<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
										)}
									</span>
									<span className="bodybold flex flex-row items-center gap-1 text-character-primary">
										Awaiting response
										<span className="opacity-100 group-hover/notePreviewBar:opacity-0">:</span>
									</span>
									<span
										role="button"
										className={`absolute -right-[7px] -top-[5px] h-[15px] w-[15px] cursor-pointer items-center justify-center rounded-full border border-solid border-grey-5 bg-white ${
											dismissing ? 'flex' : 'hidden group-hover/notePreviewBar:flex'
										}`}
										onClick={dismissing ? stopDefaultEvents : handleDismissNoteStatus}
									>
										{dismissing ? (
											<LoadingOutlined
												className="!text-black"
												style={{ fontSize: 6, width: 6, height: 6 }}
											/>
										) : (
											<CloseOutlined
												className="!text-black"
												style={{ fontSize: 6, width: 6, height: 6 }}
											/>
										)}
									</span>
								</span>
							) : nullSafeGet('isLastNoteAwaitingResponse', workOrder) ? (
								<>
									<span className="relative inline-block">
										<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-character-primary">
											<HourglassOutlined className="text-sm" />
										</span>
										{lastNoteUnread && (
											<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
										)}
									</span>
									<span className="bodybold flex flex-row items-center gap-1 whitespace-nowrap text-character-primary">
										Awaiting response:
									</span>
								</>
							) : (
								<span className="relative inline-block">
									<MessageOutlined className=" text-grey-9" />
									{lastNoteUnread && (
										<span className="absolute -right-0.5 -top-0.5 z-[1] box-border h-2 w-2 rounded-full border border-solid border-white bg-notification" />
									)}
								</span>
							)}
						</span>
						{(() => {
							const singleHighlight = nullSafeGet('highlight', workOrder);
							const singleNotesHL = nullSafeGetOrElse('notes', singleHighlight, []);
							const matchedHtml = lastNote
								? findHighlightMatch(lastNote, singleNotesHL)
								: undefined;
							if (matchedHtml) {
								return (
									<span
										className="highlights footnoteregular min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-grey-9"
										title={lastNote}
										dangerouslySetInnerHTML={{ __html: matchedHtml }}
									/>
								);
							}
							return (
								<span
									className="footnoteregular min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-grey-9"
									title={lastNote}
								>
									{lastNote}
								</span>
							);
						})()}
						<span className="footnoteregular ml-1 shrink-0 text-grey-9">-</span>
						<span className="flex shrink-0 items-center">
							<UserNameWithEmail
								mode="avatar"
								email={nullSafeGet('lastNoteAddedBy', workOrder)}
								size="small"
							/>
						</span>
						<span className="footnoteregular shrink-0 whitespace-nowrap text-grey-9">
							{' ∙ '}
							{moment(nullSafeGet('lastNoteAddedAt', workOrder)).format('hh:mm a')}
						</span>
						{!replyChannel && (
							<span
								role="button"
								className="footnoteregular ml-2 hidden shrink-0 cursor-pointer flex-row items-center gap-1 rounded border border-solid border-grey-6 bg-white px-2 text-character-primary hover:border-character-primary group-hover/notePreviewBar:inline-flex"
								onClick={(e) => {
									stopDefaultEvents(e);
									setReplyChannel('client');
								}}
							>
								<RollbackOutlined className="text-xs" />
								Reply
							</span>
						)}
					</div>
				</div>
				{replyChannel && renderReplyInput()}
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	isClientView: state.clientView.isClientView,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	refreshWOFromSearch: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrdersRestCrudThunksForSupplier.read({ id })
				: workOrdersRestCrudThunksForBuyer.read({ id })
		),
	openWorkspace: (activeTab) => dispatch(openInternalWorkspace(activeTab)),
	switchToSubcontractorView: () => dispatch(changeClientView(false)),
	dismissNoteStatus: (params) => {
		const isSupplier = ownProps.userType === ROLE_TYPES.SUPPLIER;
		const channel = params.channel || 'client';
		const thunk =
			channel === 'internal'
				? isSupplier
					? dismissWorkOrderInternalNoteStatusForSupplier
					: dismissWorkOrderInternalNoteStatusForBuyer
				: channel === 'subcontractor'
				? isSupplier
					? dismissWorkOrderContractorNoteStatusForSupplier
					: dismissWorkOrderContractorNoteStatusForBuyer
				: isSupplier
				? dismissWorkOrderNoteStatusForSupplier
				: dismissWorkOrderNoteStatusForBuyer;
		return dispatch(thunk(params));
	},
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkOrderRowNotePreview as any);
